import React, { useCallback, useEffect, useState } from "react";
import { HandHeart, ShieldCheck, XCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/auth";

/**
 * EA-1 Slice 3 — Patient Assistance Transparency Card.
 *
 * Consumes only:
 *   GET  /api/intake/me/assistance
 *   POST /api/intake/me/assistance/withdraw
 *
 * Never exposes staff email, staff id, correlation ids, audit hashes,
 * or intake notes.  No import of Northstar/Ask/JourneyMemory/
 * Personalization/Buddy modules.  Renders `null` when the patient has
 * no assistance history (spec §10 empty state).
 */

function fmt(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return "—";
  }
}

function statusTone(status) {
  if (status === "Active") return "bg-[#2D9B4E]/10 border-[#2D9B4E]/40 text-[#0B132B]";
  if (status === "Expired") return "bg-[#FFF8EC] border-[#F5A524]/40 text-[#0B132B]";
  return "bg-[#F8F9FB] border-gray-200 text-[#0B132B]";
}

export default function AssistanceTransparencyCard({ variant = "onboarding" }) {
  const [data, setData] = useState(undefined); // undefined = loading, null = none, obj = payload
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/intake/me/assistance");
      setData(data);
    } catch {
      setData(null);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (data === undefined) return null;
  if (!data || !data.has_assistance_history) return null;

  const active = data.active_session;
  const shown = active || data.most_recent;
  if (!shown) return null;

  const withdraw = async () => {
    setBusy(true);
    try {
      await api.post("/intake/me/assistance/withdraw");
      toast.success("Assistance ended. Your saved information is unchanged.");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not end assistance.");
    } finally {
      setBusy(false);
    }
  };

  const compact = variant === "dashboard";

  return (
    <section
      className={`rounded-2xl border ${statusTone(shown.assistance_status)} ${compact ? "p-4" : "p-5 sm:p-6"} ${compact ? "" : "mt-6"}`}
      data-testid="assistance-transparency"
      aria-labelledby="assistance-transparency-heading"
    >
      <header className="flex items-start gap-3">
        <div className="mt-0.5 w-9 h-9 rounded-full bg-white flex items-center justify-center shrink-0 border border-current/20">
          {shown.assistance_status === "Active" ? (
            <ShieldCheck className="w-5 h-5 text-[#23803F]" aria-hidden="true" />
          ) : (
            <HandHeart className="w-5 h-5 text-[#B45309]" aria-hidden="true" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#4B5563]">
            Assistance with your intake
          </p>
          <h3
            id="assistance-transparency-heading"
            className={`font-extrabold text-[#0B132B] ${compact ? "text-base" : "text-lg"}`}
          >
            {shown.assistance_status === "Active"
              ? "BTC is assisting you with this intake."
              : "BTC assisted you with this intake."}
          </h3>
          <p className="text-sm text-[#4B5563] mt-1">
            BTC staff helped enter information during your intake. You still
            control final submission.
          </p>
        </div>
        <span
          className="shrink-0 inline-flex items-center px-3 py-1 rounded-full text-[11px] font-semibold border border-current/30 bg-white/70"
          data-testid="assistance-status-badge"
          aria-label={`Assistance status: ${shown.assistance_status}`}
        >
          {shown.assistance_status}
        </span>
      </header>

      <dl className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-x-4 gap-y-2 text-xs">
        <div>
          <dt className="uppercase tracking-wider text-[#4B5563]">Assisted by</dt>
          <dd
            className="font-semibold text-[#0B132B] mt-0.5"
            data-testid="assistance-assisted-by"
          >
            {shown.assisted_by_display_name}
          </dd>
        </div>
        <div>
          <dt className="uppercase tracking-wider text-[#4B5563]">Started</dt>
          <dd
            className="font-semibold text-[#0B132B] mt-0.5"
            data-testid="assistance-started-at"
          >
            {fmt(shown.assisted_at)}
          </dd>
        </div>
        <div>
          <dt className="uppercase tracking-wider text-[#4B5563]">
            {shown.assistance_status === "Active" ? "Expires" : "Ended"}
          </dt>
          <dd
            className="font-semibold text-[#0B132B] mt-0.5"
            data-testid="assistance-endpoint-time"
          >
            {fmt(
              shown.assistance_status === "Active"
                ? shown.expires_at
                : shown.ended_at || shown.expires_at
            )}
          </dd>
        </div>
      </dl>

      {active && (
        <div className="mt-4 pt-4 border-t border-current/15 flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={withdraw}
            disabled={busy}
            data-testid="assistance-patient-withdraw"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#B45309]/60 bg-white text-[#B45309] text-xs font-semibold hover:bg-[#FFF8EC] disabled:opacity-50 transition"
          >
            {busy ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden="true" />
            ) : (
              <XCircle className="w-3.5 h-3.5" aria-hidden="true" />
            )}
            End Assistance
          </button>
          <p className="text-xs text-[#4B5563]">
            Ending assistance does not delete anything you have already saved.
          </p>
        </div>
      )}
    </section>
  );
}
